import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiSend, FiCornerUpLeft } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { datosAPI, catalogosAPI, flujoAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import ModalEnviarFlujo from '../components/ModalEnviarFlujo';

const FormularioDato = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [catalogos, setCatalogos] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDevolverOpen, setModalDevolverOpen] = useState(false);
  const [datoActual, setDatoActual] = useState(null);
  
  const [formData, setFormData] = useState({
    tema: '',
    palabrasClave: '',
    descripcion: '',
    textoHistorico: '',
    imagen: '',
    strongHebreo: '',
    strongGriego: '',
    citaBiblica: '',
    referencias: '',
    analisisEncontrado: '',
    hechosHistoricos: '',
    fechaEvento: '',
    porcentajeHistoricidad: '',
    porcentajeAutenticidad: '',
    porcentajeVeracidad: '',
    prioridad: 'media',
    criticidad: 'media',
    tiempoInvestigacionHoras: '',
    catalogosSeleccionados: [],
    estado: 'pendiente'
  });

  const [errors, setErrors] = useState({});

  // Determinar la ruta de retorno basada en el rol
  const getRutaRetorno = () => {
    if (hasRole('gestor')) return '/historicidad/gestor';
    if (hasRole('validador')) return '/historicidad/validador';
    if (hasRole('editor')) return '/historicidad/editor';
    return '/historicidad';
  };

  useEffect(() => {
    // Verificar que el usuario tenga un rol de flujo
    if (!hasRole('gestor') && !hasRole('validador') && !hasRole('editor')) {
      navigate('/');
      return;
    }
    cargarCatalogos();
    if (id) {
      cargarDato();
    }
  }, [id]);

  const cargarCatalogos = async () => {
    try {
      const response = await catalogosAPI.listar();
      setCatalogos(response.data.data);
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };

  const cargarDato = async () => {
    try {
      setLoading(true);
      const response = await datosAPI.getPorId(id);
      const dato = response.data.data;
      
      // Guardar el dato completo para usar en el modal
      setDatoActual(dato);
      
      setFormData({
        tema: dato.tema || '',
        palabrasClave: dato.palabrasClave || '',
        descripcion: dato.descripcion || '',
        textoHistorico: dato.textoHistorico || '',
        imagen: dato.imagen || '',
        strongHebreo: dato.strongHebreo || '',
        strongGriego: dato.strongGriego || '',
        citaBiblica: dato.citaBiblica || '',
        referencias: dato.referencias || '',
        analisisEncontrado: dato.analisisEncontrado || '',
        hechosHistoricos: dato.hechosHistoricos || '',
        fechaEvento: dato.fechaEvento ? dato.fechaEvento.split('T')[0] : '',
        porcentajeHistoricidad: dato.porcentajeHistoricidad || '',
        porcentajeAutenticidad: dato.porcentajeAutenticidad || '',
        porcentajeVeracidad: dato.porcentajeVeracidad || '',
        prioridad: dato.prioridad || 'media',
        criticidad: dato.criticidad || 'media',
        tiempoInvestigacionHoras: dato.tiempoInvestigacionHoras || '',
        catalogosSeleccionados: dato.catalogos?.map(c => c.id) || [],
        estado: dato.estado || 'pendiente'
      });
    } catch (error) {
      console.error('Error al cargar dato:', error);
      navigate(getRutaRetorno());
    } finally {
      setLoading(false);
    }
  };

  const validarFormulario = () => {
    const newErrors = {};

    if (!formData.tema.trim()) {
      newErrors.tema = 'El tema es requerido';
    }

    if (!formData.descripcion.trim()) {
      newErrors.descripcion = 'La descripción es requerida';
    }

    if (!formData.textoHistorico.trim() || formData.textoHistorico === '<p><br></p>') {
      newErrors.textoHistorico = 'El texto histórico es requerido';
    }

    if (formData.catalogosSeleccionados.length === 0) {
      newErrors.catalogos = 'Selecciona al menos un catálogo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, guardarComoBorrador = false) => {
    if (e) e.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        catalogos: formData.catalogosSeleccionados
      };

      // SOLO establecer el estado si:
      // 1. Es un dato NUEVO (sin id)
      // 2. Es un borrador existente (estado 'pendiente') y queremos activarlo
      // 3. Estamos guardando explícitamente como borrador
      if (!id) {
        // Dato nuevo
        dataToSend.estado = guardarComoBorrador ? 'pendiente' : 'creada_gestor';
      } else {
        // Dato existente: solo cambiar estado si está en 'pendiente' y queremos activarlo
        const estadoActual = datoActual?.estado || formData.estado;
        if (guardarComoBorrador) {
          // Forzar a borrador solo si está en 'creada_gestor' o 'pendiente'
          if (estadoActual === 'creada_gestor' || estadoActual === 'pendiente') {
            dataToSend.estado = 'pendiente';
          }
        } else {
          // Activar solo si está en 'pendiente'
          if (estadoActual === 'pendiente') {
            dataToSend.estado = 'creada_gestor';
          }
          // Si está en otro estado (informacion_valida, etc), NO cambiar el estado
        }
      }

      if (id) {
        await datosAPI.actualizar(id, dataToSend);
        // Recargar el dato para actualizar el estado
        await cargarDato();
      } else {
        const response = await datosAPI.crear(dataToSend);
        // Si es nuevo, navegar a editar para que pueda enviar
        const nuevoId = response.data.data.id;
        navigate(getRutaRetorno() + `/editar/${nuevoId}`);
      }
    } catch (error) {
      console.error('Error al guardar dato:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const guardarComoBorrador = () => {
    handleSubmit(null, true);
  };

  const guardarParaEnviar = () => {
    handleSubmit(null, false);
  };

  const abrirModalEnvio = () => {
    setModalOpen(true);
  };

  const cerrarModal = () => {
    setModalOpen(false);
  };

  const abrirModalDevolver = () => {
    setModalDevolverOpen(true);
  };

  const cerrarModalDevolver = () => {
    setModalDevolverOpen(false);
  };

  const confirmarEnvio = async (motivo) => {
    try {
      // Determinar el estado destino según el estado actual
      let estadoDestino = '';
      const estadoActual = datoActual?.estado;

      if (estadoActual === 'creada_gestor') {
        estadoDestino = 'informacion_valida';
      } else if (estadoActual === 'informacion_valida') {
        estadoDestino = 'informacion_contrastada_editor';
      } else if (estadoActual === 'informacion_contrastada_editor') {
        estadoDestino = 'informacion_aprobada';
      }

      await flujoAPI.transicionar(
        id,
        estadoDestino,
        motivo
      );
      navigate(getRutaRetorno());
    } catch (error) {
      console.error('Error al enviar:', error);
      throw error;
    }
  };

  const confirmarDevolver = async (motivo) => {
    try {
      // Determinar el estado anterior según el estado actual
      let estadoAnterior = '';
      const estadoActual = datoActual?.estado;

      if (estadoActual === 'informacion_valida') {
        estadoAnterior = 'creada_gestor'; // Validador devuelve a Gestor
      } else if (estadoActual === 'informacion_contrastada_editor') {
        estadoAnterior = 'informacion_valida'; // Editor devuelve a Validador
      }

      await flujoAPI.transicionar(
        id,
        estadoAnterior,
        motivo
      );
      navigate(getRutaRetorno());
    } catch (error) {
      console.error('Error al devolver:', error);
      throw error;
    }
  };

  const handleCatalogoToggle = (catalogoId) => {
    setFormData(prev => ({
      ...prev,
      catalogosSeleccionados: prev.catalogosSeleccionados.includes(catalogoId)
        ? prev.catalogosSeleccionados.filter(id => id !== catalogoId)
        : [...prev.catalogosSeleccionados, catalogoId]
    }));
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link', 'image'],
      [{ 'align': [] }],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
  };

  if (loading && id) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to={getRutaRetorno()}
                className="text-gray-600 hover:text-gray-900"
              >
                <FiArrowLeft size={24} />
              </Link>
              <h1 className="text-2xl font-bold text-gray-900">
                {id ? 'Editar Dato Histórico' : 'Nuevo Dato Histórico'}
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={guardarComoBorrador}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition"
                title="Guardar sin activar el flujo de validación"
              >
                <FiSave className="mr-2" />
                {loading ? 'Guardando...' : 'Guardar como Borrador'}
              </button>
              <button
                onClick={guardarParaEnviar}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                title="Guardar y activar opción de envío a validación"
              >
                <FiSave className="mr-2" />
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
              {id && datoActual?.estado === 'creada_gestor' && hasRole('gestor') && (
                <button
                  onClick={abrirModalEnvio}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
                  title="Enviar a validación"
                >
                  <FiSend className="mr-2" />
                  Enviar a Validación
                </button>
              )}
              {id && datoActual?.estado === 'informacion_valida' && hasRole('validador') && (
                <>
                  <button
                    onClick={abrirModalDevolver}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition"
                    title="Devolver a Gestor"
                  >
                    <FiCornerUpLeft className="mr-2" />
                    Devolver a Gestor
                  </button>
                  <button
                    onClick={abrirModalEnvio}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
                    title="Enviar a Editor"
                  >
                    <FiSend className="mr-2" />
                    Enviar a Editor
                  </button>
                </>
              )}
              {id && datoActual?.estado === 'informacion_contrastada_editor' && hasRole('editor') && (
                <>
                  <button
                    onClick={abrirModalDevolver}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition"
                    title="Devolver a Validador"
                  >
                    <FiCornerUpLeft className="mr-2" />
                    Devolver a Validador
                  </button>
                  <button
                    onClick={abrirModalEnvio}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
                    title="Aprobar y Publicar"
                  >
                    <FiSend className="mr-2" />
                    Aprobar y Publicar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Formulario */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información Básica */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Información Básica</h2>
            
            <div className="space-y-4">
              {/* Tema */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tema *
                </label>
                <input
                  type="text"
                  value={formData.tema}
                  onChange={(e) => setFormData({ ...formData, tema: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Título del dato histórico"
                />
                {errors.tema && <p className="text-red-600 text-sm mt-1">{errors.tema}</p>}
              </div>

              {/* Palabras Clave */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Palabras Clave
                </label>
                <input
                  type="text"
                  value={formData.palabrasClave}
                  onChange={(e) => setFormData({ ...formData, palabrasClave: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Separadas por comas: arqueología, Jericó, excavación..."
                />
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción Breve *
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Resumen breve que aparecerá en las tarjetas"
                />
                {errors.descripcion && <p className="text-red-600 text-sm mt-1">{errors.descripcion}</p>}
              </div>

              {/* Imagen URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL de Imagen
                </label>
                <input
                  type="url"
                  value={formData.imagen}
                  onChange={(e) => setFormData({ ...formData, imagen: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://images.unsplash.com/..."
                />
                {formData.imagen && (
                  <img
                    src={formData.imagen}
                    alt="Preview"
                    className="mt-2 h-32 w-auto rounded object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Texto Histórico */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Contenido Histórico *</h2>
            <ReactQuill
              theme="snow"
              value={formData.textoHistorico}
              onChange={(value) => setFormData({ ...formData, textoHistorico: value })}
              modules={modules}
              className="bg-white"
              style={{ minHeight: '300px' }}
            />
            {errors.textoHistorico && <p className="text-red-600 text-sm mt-1">{errors.textoHistorico}</p>}
          </div>

          {/* Referencias Bíblicas */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Referencias Bíblicas</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Strong Hebreo
                </label>
                <input
                  type="text"
                  value={formData.strongHebreo}
                  onChange={(e) => setFormData({ ...formData, strongHebreo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="H3405 (Yericho - יְרִיחוֹ)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Strong Griego
                </label>
                <input
                  type="text"
                  value={formData.strongGriego}
                  onChange={(e) => setFormData({ ...formData, strongGriego: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="G2410 (Hiericho - Ἱεριχώ)"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cita Bíblica
                </label>
                <textarea
                  value={formData.citaBiblica}
                  onChange={(e) => setFormData({ ...formData, citaBiblica: e.target.value })}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Josué 6:1-27, Hebreos 11:30"
                />
              </div>
            </div>
          </div>

          {/* Análisis e Investigación */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Análisis e Investigación</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Análisis Encontrado
                </label>
                <textarea
                  value={formData.analisisEncontrado}
                  onChange={(e) => setFormData({ ...formData, analisisEncontrado: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Análisis de la evidencia encontrada..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hechos Históricos Relacionados
                </label>
                <textarea
                  value={formData.hechosHistoricos}
                  onChange={(e) => setFormData({ ...formData, hechosHistoricos: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Hechos históricos relacionados..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Referencias Bibliográficas
                </label>
                <textarea
                  value={formData.referencias}
                  onChange={(e) => setFormData({ ...formData, referencias: e.target.value })}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Wood, Bryant G. 'Did the Israelites Conquer Jericho?' Biblical Archaeology Review, 1990"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha del Evento Histórico
                </label>
                <input
                  type="date"
                  value={formData.fechaEvento}
                  onChange={(e) => setFormData({ ...formData, fechaEvento: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Porcentajes de Evaluación */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Evaluación (Porcentajes)</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Historicidad (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="99.99"
                  step="0.01"
                  value={formData.porcentajeHistoricidad}
                  onChange={(e) => setFormData({ ...formData, porcentajeHistoricidad: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="85.50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Autenticidad (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="99.99"
                  step="0.01"
                  value={formData.porcentajeAutenticidad}
                  onChange={(e) => setFormData({ ...formData, porcentajeAutenticidad: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="82.75"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Veracidad (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="99.99"
                  step="0.01"
                  value={formData.porcentajeVeracidad}
                  onChange={(e) => setFormData({ ...formData, porcentajeVeracidad: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="80.25"
                />
              </div>
            </div>
          </div>

          {/* Prioridad y Gestión */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Prioridad y Gestión</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prioridad
                </label>
                <select
                  value={formData.prioridad}
                  onChange={(e) => setFormData({ ...formData, prioridad: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="baja">Baja</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Criticidad
                </label>
                <select
                  value={formData.criticidad}
                  onChange={(e) => setFormData({ ...formData, criticidad: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="baja">Baja</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tiempo de Investigación (hrs)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.tiempoInvestigacionHoras}
                  onChange={(e) => setFormData({ ...formData, tiempoInvestigacionHoras: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="45.5"
                />
              </div>
            </div>
          </div>

          {/* Catálogos */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Catálogos *</h2>
            {errors.catalogos && <p className="text-red-600 text-sm mb-4">{errors.catalogos}</p>}
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {catalogos.map((catalogo) => (
                <label
                  key={catalogo.id}
                  className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                  style={{
                    borderColor: formData.catalogosSeleccionados.includes(catalogo.id)
                      ? catalogo.color
                      : '#d1d5db',
                    backgroundColor: formData.catalogosSeleccionados.includes(catalogo.id)
                      ? `${catalogo.color}10`
                      : 'white'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.catalogosSeleccionados.includes(catalogo.id)}
                    onChange={() => handleCatalogoToggle(catalogo.id)}
                    className="rounded text-blue-600 focus:ring-blue-500"
                    style={{
                      accentColor: catalogo.color
                    }}
                  />
                  <span className="text-sm font-medium">{catalogo.nombre}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-4">
            <Link
              to={getRutaRetorno()}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>

      {/* Modal de confirmación de envío */}
      {/* Modal para Enviar */}
      <ModalEnviarFlujo
        isOpen={modalOpen}
        onClose={cerrarModal}
        onConfirm={confirmarEnvio}
        datoTema={formData.tema}
        estadoActual={datoActual?.estado || 'creada_gestor'}
        siguienteRol={
          datoActual?.estado === 'creada_gestor' ? 'Validador' :
          datoActual?.estado === 'informacion_valida' ? 'Editor' :
          'Publicación'
        }
      />

      {/* Modal para Devolver */}
      <ModalEnviarFlujo
        isOpen={modalDevolverOpen}
        onClose={cerrarModalDevolver}
        onConfirm={confirmarDevolver}
        datoTema={formData.tema}
        estadoActual={datoActual?.estado}
        siguienteRol={
          datoActual?.estado === 'informacion_valida' ? 'Gestor' :
          datoActual?.estado === 'informacion_contrastada_editor' ? 'Validador' :
          'Rol anterior'
        }
        esDevolucion={true}
      />
    </div>
  );
};

export default FormularioDato;

